
import { ApiUrls } from '@api/api.urls';
import { makeAxiosRequest } from '@api/apiUtils/makeAxiosRequest';
import { makeDynamicApiRoute } from '@navigation/routes.utilities';

import type {
  AIReviewFetchResponse,
  AIReviewStatsResponse,
  FastReviewPayload,
  MyReviewsResponse,
  PublicAIReviewFetchResponse,
  SubmitFeedbackPayload,
  TogglePublicSharingPayload,
  UpdateSharedEmailsPayload,
} from './api.aiReviews.types';

// Note: createAiReview returns AIReviewFetchResponse (201 Created)

export const retrieveAiReview = (id: string) => {
  const url = makeDynamicApiRoute(ApiUrls.aiReviews.retrieve, { id });
  return makeAxiosRequest<AIReviewFetchResponse>({ url, method: 'get' });
};

export const deleteAiReview = (id: string) => {
  const url = makeDynamicApiRoute(ApiUrls.aiReviews.destroy, { id });
  return makeAxiosRequest<undefined>({ url, method: 'delete' });
};

export const retrievePublicAiReview = (id: string) => {
  const url = makeDynamicApiRoute(ApiUrls.aiReviews.public, { id });
  return makeAxiosRequest<PublicAIReviewFetchResponse>({ url, method: 'get' });
};

export const togglePublicSharing = (
  id: string,
  payload: TogglePublicSharingPayload,
) => {
  const url = makeDynamicApiRoute(ApiUrls.aiReviews.togglePublicSharing, { id });
  return makeAxiosRequest<AIReviewFetchResponse, TogglePublicSharingPayload>({
    url,
    method: 'post',
    payload,
  });
};

export const updateSharedEmails = (
  id: string,
  payload: UpdateSharedEmailsPayload,
) => {
  const url = makeDynamicApiRoute(ApiUrls.aiReviews.updateSharedEmails, { id });
  return makeAxiosRequest<AIReviewFetchResponse, UpdateSharedEmailsPayload>({
    url,
    method: 'post',
    payload,
  });
};

export const createAiReview = (
  payload: FastReviewPayload,
  onUploadProgress?: (event: import('axios').AxiosProgressEvent) => void,
) => {
  const formData = new FormData();
  formData.append('file', payload.file);
  formData.append('user_consent', String(payload.user_consent));

  return makeAxiosRequest<AIReviewFetchResponse>({
    url: ApiUrls.aiReviews.fastReview,
    method: 'post',
    payload: formData as unknown as Record<string, unknown>,
    contentType: 'multipart/form-data',
    onUploadProgress,
  });
};

export const retrieveMyReviews = () => {
  return makeAxiosRequest<MyReviewsResponse>({
    url: ApiUrls.aiReviews.myReviews,
    method: 'get',
  });
};

export const retrieveLastReview = () => {
  return makeAxiosRequest<AIReviewFetchResponse>({
    url: ApiUrls.aiReviews.lastReview,
    method: 'get',
  });
};

export const submitFeedback = (
  id: string,
  payload: SubmitFeedbackPayload,
) => {
  const url = makeDynamicApiRoute(ApiUrls.aiReviews.submitFeedback, { id });
  return makeAxiosRequest<AIReviewFetchResponse, SubmitFeedbackPayload>({
    url,
    method: 'post',
    payload,
  });
};

export const cancelAiReview = (id: string) => {
  const url = makeDynamicApiRoute(ApiUrls.aiReviews.cancel, { id });
  return makeAxiosRequest<AIReviewFetchResponse>({ url, method: 'post' });
};

export const restartAiReview = (id: string) => {
  const url = makeDynamicApiRoute(ApiUrls.aiReviews.restart, { id });
  return makeAxiosRequest<AIReviewFetchResponse>({ url, method: 'post' });
};

export const retrieveAiReviewsStats = () => {
  return makeAxiosRequest<AIReviewStatsResponse>({
    url: ApiUrls.aiReviews.stats,
    method: 'get',
  });
};
